import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Badge,
  Box,
  Button,
  Group,
  Progress,
  Select,
  SimpleGrid,
  Stack,
  Table,
  Text,
  TextInput,
} from '@mantine/core';
import {
  ABUSE_SOURCE_LABELS,
  ABUSE_STATUSES,
  EXTERNAL_ABUSE_SOURCES,
  type ExternalAbuseSource,
} from '@seo/contracts';
import { publicMessageOf, useTRPC } from '../lib/trpc';
import { useCan } from '../lib/session';
import classes from './EntityPage.module.css';

const KIND_LABELS: Record<string, string> = {
  down: 'Недоступний',
  index_drop: 'Просадка індексації',
  deploy_anomaly: 'Аномалія після деплою',
};

const STATUS_LABELS: Record<string, string> = {
  new: 'НОВИЙ',
  response: 'ВІДПОВІДЬ',
  migration: 'МІГРАЦІЯ',
  rewrite: 'РЕ-РАЙТ',
  closed: 'ЗАКРИТО',
  frozen: 'ЗАМОРОЖ.',
};

const SOURCE_HINTS: Record<ExternalAbuseSource, string> = {
  dmca: 'скарга правовласника',
  cloudflare: 'phishing / abuse flag зони',
};

/**
 * Моніторинг та абузи (§7), сценарій приймання 12.
 *
 * Дві половини на одному екрані навмисно: оператор, який дивиться на 5xx, мусить тим самим
 * поглядом бачити, чи домен ще й під абузом — бо саме це вирішує, відповідь тут «перезапустити»
 * чи «не чіпати».
 *
 * Кнопок, що запускають розклад, тут більше немає. «Пінгувати зараз» і ручний перерахунок
 * показували стан на момент кліку й приховували головне — що розклад міг стояти тиждень.
 * Замість них шість плиток мокапа (`monKpis`) і розподіл кодів (`indexRows`): вони показують
 * сам стан і не залежать від того, чи хтось клікнув.
 *
 * Форма заведення кейса лишилась, але звузилась до DMCA і Cloudflare. Решту трьох джерел
 * система тепер бачить сама — `detectAbuse` заводить і закриває їх за фактом спостереження,
 * і другий, доданий руками кейс на те саме падіння правило не впізнало б і не закрило.
 */
export function MonitoringPage(): React.JSX.Element {
  const trpc = useTRPC();
  const queryClient = useQueryClient();
  const canWrite = useCan('monitoring', 'write');

  const [siteId, setSiteId] = useState<string | null>(null);
  const [source, setSource] = useState<ExternalAbuseSource>('dmca');
  const [reason, setReason] = useState('');

  const sites = useQuery(
    trpc.sites.list.queryOptions({ pagination: { limit: 200, direction: 'desc' } }),
  );
  const kpis = useQuery(trpc.monitoring.kpis.queryOptions());
  const breakdown = useQuery(trpc.monitoring.indexBreakdown.queryOptions());
  const incidents = useQuery(trpc.monitoring.incidents.queryOptions({ open: true }));
  const abuseCases = useQuery(trpc.monitoring.abuseCases.queryOptions({}));
  const scores = useQuery(trpc.monitoring.seoScores.queryOptions({}));

  const invalidate = async (): Promise<void> => {
    await queryClient.invalidateQueries({ queryKey: trpc.monitoring.pathKey() });
  };

  const registerCase = useMutation(
    trpc.monitoring.registerAbuseCase.mutationOptions({
      onSuccess: async () => {
        setReason('');
        await invalidate();
      },
    }),
  );
  const updateCase = useMutation(
    trpc.monitoring.updateAbuseCase.mutationOptions({ onSuccess: invalidate }),
  );

  return (
    <Stack gap="md">
      <SimpleGrid cols={{ base: 2, md: 3, lg: 6 }} spacing="xs" data-testid="monitoring-kpis">
        {(kpis.data ?? []).map((kpi) => (
          <Box key={kpi.key} className={classes.tile} data-testid={`mon-kpi-${kpi.key}`}>
            <Text className={classes.tileLabel}>{kpi.label}</Text>
            <Text className={kpi.level === 'ok' ? classes.tileValue : classes.tileValueDanger}>
              {kpi.value}
            </Text>
            <Text className={classes.dim} data-testid={`mon-kpi-note-${kpi.key}`}>
              {kpi.note}
            </Text>
          </Box>
        ))}
      </SimpleGrid>

      <Box className={classes.panel} data-testid="monitoring-incidents">
        <Text className={classes.panelTitle} mb={8}>
          Відкриті інциденти
        </Text>

        {(incidents.data ?? []).length === 0 ? (
          <Text className={classes.mono} data-testid="monitoring-no-incidents">
            ✓ відкритих інцидентів немає
          </Text>
        ) : (
          <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th>Домен</Table.Th>
                <Table.Th>Тип</Table.Th>
                <Table.Th>Відкрито</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {(incidents.data ?? []).map((incident) => (
                <Table.Tr key={incident.id} data-testid={`incident-${incident.domain}`}>
                  <Table.Td className={classes.mono}>{incident.domain}</Table.Td>
                  <Table.Td>
                    <Badge size="xs" color="rose" data-testid={`incident-kind-${incident.domain}`}>
                      {KIND_LABELS[incident.kind] ?? incident.kind}
                    </Badge>
                  </Table.Td>
                  <Table.Td className={classes.dim}>
                    {incident.openedAt.toISOString().slice(0, 16)}
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        )}
      </Box>

      <Box className={classes.panel} data-testid="monitoring-index">
        <Text className={classes.panelTitle} mb={8}>
          Коди відповіді мережі
        </Text>
        <Stack gap={6}>
          {(breakdown.data ?? []).map((row) => (
            <Group key={row.key} gap={10} data-testid={`index-row-${row.key}`}>
              <Text className={classes.mono} fz="xs" style={{ width: 90 }}>
                {row.label}
              </Text>
              <Progress
                value={row.pct}
                size="sm"
                color={row.key === '200' ? 'mint' : row.key === '5xx' ? 'rose' : 'amber'}
                style={{ flex: 1 }}
              />
              <Text className={classes.dim} style={{ width: 130 }}>
                {row.sites} доменів · {row.pct}%
              </Text>
            </Group>
          ))}
        </Stack>
      </Box>

      <Box className={classes.panel} data-testid="monitoring-abuse">
        <Text className={classes.panelTitle} mb={8}>
          Абуз-кейси
        </Text>

        {canWrite ? (
          <Group gap="xs" align="flex-end" mb="sm">
            <Select
              label="Сайт"
              placeholder="домен"
              data={(sites.data?.items ?? []).map((site) => ({
                value: site.id,
                label: site.domain,
              }))}
              value={siteId}
              onChange={setSiteId}
              searchable
              size="xs"
              data-testid="abuse-site"
              style={{ width: 220 }}
            />
            <Select
              label="Джерело"
              description="хостинг, реєстратор і Google система заводить сама"
              data={EXTERNAL_ABUSE_SOURCES.map((value) => ({
                value,
                label: `${ABUSE_SOURCE_LABELS[value]} — ${SOURCE_HINTS[value]}`,
              }))}
              value={source}
              onChange={(value) => {
                setSource(value ?? 'dmca');
              }}
              size="xs"
              data-testid="abuse-source"
              style={{ width: 260 }}
            />
            <TextInput
              label="Причина"
              size="xs"
              value={reason}
              onChange={(event) => {
                setReason(event.currentTarget.value);
              }}
              data-testid="abuse-reason"
              style={{ width: 280 }}
            />
            <Button
              size="xs"
              disabled={siteId === null || reason.trim() === ''}
              loading={registerCase.isPending}
              data-testid="abuse-open"
              onClick={() => {
                if (siteId !== null) {
                  registerCase.mutate({ siteId, source, reason });
                }
              }}
            >
              Зареєструвати кейс
            </Button>
          </Group>
        ) : null}

        {registerCase.error === null ? null : (
          <Text className={classes.danger} data-testid="abuse-error">
            {publicMessageOf(registerCase.error)}
          </Text>
        )}

        <Table striped withRowBorders={false} verticalSpacing={4} fz="xs">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>Домен</Table.Th>
              <Table.Th>Джерело</Table.Th>
              <Table.Th>Причина</Table.Th>
              <Table.Th>Дата</Table.Th>
              <Table.Th>Статус</Table.Th>
              <Table.Th />
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {(abuseCases.data ?? []).map((row) => (
              <Table.Tr key={row.id} data-testid={`abuse-row-${row.domain}`}>
                <Table.Td className={classes.mono}>{row.domain}</Table.Td>
                <Table.Td>
                  <Badge
                    size="xs"
                    color={row.critical ? 'rose' : 'graphite'}
                    data-testid={`abuse-critical-${row.domain}`}
                  >
                    {ABUSE_SOURCE_LABELS[row.source] ?? row.source}
                    {row.critical ? ' · критичний' : ''}
                  </Badge>
                </Table.Td>
                <Table.Td className={classes.dim}>
                  {row.reason}
                  {/*
                   * Кейс, заведений правилом, підписаний як такий: оператор має бачити, що
                   * цей закриється сам, щойно умова зникне, і що змінений вручну статус
                   * забирає його з-під правила назавжди.
                   */}
                  {row.autoRule === null ? null : (
                    <Text
                      component="span"
                      className={classes.mono}
                      data-testid={`abuse-auto-${row.domain}`}
                    >
                      {' '}
                      · авто
                    </Text>
                  )}
                </Table.Td>
                <Table.Td className={classes.dim}>
                  {row.receivedAt.toISOString().slice(0, 10)}
                </Table.Td>
                <Table.Td>
                  <Select
                    size="xs"
                    data={ABUSE_STATUSES.map((value) => ({
                      value,
                      label: STATUS_LABELS[value] ?? value,
                    }))}
                    value={row.status}
                    disabled={!canWrite}
                    data-testid={`abuse-status-${row.domain}`}
                    onChange={(value) => {
                      if (value !== null) {
                        updateCase.mutate({ id: row.id, status: value });
                      }
                    }}
                    style={{ width: 140 }}
                  />
                </Table.Td>
                <Table.Td>
                  {row.critical && row.status !== 'closed' ? (
                    <Text className={classes.warn} data-testid={`abuse-frozen-${row.domain}`}>
                      виключено з масових операцій
                    </Text>
                  ) : null}
                </Table.Td>
              </Table.Tr>
            ))}
          </Table.Tbody>
        </Table>
      </Box>

      <Box className={classes.panel} data-testid="monitoring-scores">
        <Text className={classes.panelTitle} mb={8}>
          SEO-скор
        </Text>
        {(scores.data ?? []).length === 0 ? (
          <Text className={classes.dim} data-testid="monitoring-no-scores">
            Скор ще не рахувався.
          </Text>
        ) : (
          <Stack gap={4}>
            {(scores.data ?? []).slice(-20).map((score) => (
              <Group
                key={`${score.siteId}-${score.date}`}
                gap={10}
                data-testid={`score-${score.domain}`}
              >
                <Text className={classes.mono} fz="xs" style={{ width: 200 }}>
                  {score.domain}
                </Text>
                <Badge
                  size="xs"
                  color={score.total >= 70 ? 'mint' : score.total >= 40 ? 'amber' : 'rose'}
                >
                  {score.total}
                </Badge>
                <Text className={classes.dim}>
                  тех {score.techBase} · ядро {score.coreCoverage} · унікальність{' '}
                  {score.uniqueness}
                </Text>
              </Group>
            ))}
          </Stack>
        )}
      </Box>
    </Stack>
  );
}
